// =======================================
// 6) UI DYNAMIQUE: src/app/modules/chasseurs/ChasseurDeTete.jsx
// (reprend ton UI + redux + sagas)
// =======================================

import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Plus,
    X,
    Trash2,
    Eye,
    Edit2,
    Users,
    Mail,
    Link as LinkIcon,
    Search,
    Upload,
} from "lucide-react";

import "./ChasseurDeTete.css";

import {
    fetchChasseursRequest,
    createChasseurRequest,
    updateChasseurRequest,
    deleteChasseurRequest,
    fetchDomainesRequest,
} from "../../../../../redux/actions/chasseursActions";

import { API_BASE_URL } from "../../../../../../api";


const Modal = ({ open, title, children, footer, onClose }) => {
    if (!open) return null;
    return (
        <div className="chdModal" role="dialog" aria-modal="true" onMouseDown={onClose}>
            <div className="chdModal__panel" onMouseDown={(e) => e.stopPropagation()}>
                <div className="chdModal__header">
                    <div className="chdModal__title">{title}</div>
                    <button className="chdIconBtn" onClick={onClose} aria-label="Fermer" type="button">
                        <X size={18} />
                    </button>
                </div>
                <div className="chdModal__body">{children}</div>
                {footer && <div className="chdModal__footer">{footer}</div>}
            </div>
        </div>
    );
};

export default function ChasseurDeTete() {
    const dispatch = useDispatch();

    const { chasseurs, domaines, loading } = useSelector((s) => s.chasseurs);

    // UI state
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const [selectedChasseur, setSelectedChasseur] = useState(null);

    const [form, setForm] = useState({
        nom: "",
        description: "",
        email: "",
        siteweb: "",
        domaineIds: [],
        imageFile: null,
        picturePreview: null,
    });

    const [filters, setFilters] = useState({ nom: "", email: "" });

    // ===== Fetch initial =====
    useEffect(() => {
        dispatch(fetchChasseursRequest());
        dispatch(fetchDomainesRequest());
    }, [dispatch]);

    // ===== Helper: domains map =====
    const domainesById = useMemo(() => {
        const m = {};
        (domaines || []).forEach((d) => {
            m[d.id] = d;
        });
        return m;
    }, [domaines]);

    // ===== Filtered list =====
    const filteredChasseurs = useMemo(() => {
        let filtered = [...(chasseurs || [])];

        if (filters.nom) {
            filtered = filtered.filter((c) => (c.nom || "").toLowerCase().includes(filters.nom.toLowerCase()));
        }
        if (filters.email) {
            filtered = filtered.filter((c) => (c.email || "").toLowerCase().includes(filters.email.toLowerCase()));
        }
        return filtered;
    }, [chasseurs, filters]);

    const isAnyFilterFilled = filters.nom || filters.email;

    const resetForm = () => {
        setForm({
            nom: "",
            description: "",
            email: "",
            siteweb: "",
            domaineIds: [],
            imageFile: null,
            picturePreview: null,
        });
    };

    const openCreate = () => {
        resetForm();
        setShowAddModal(true);
    };

    const closeCreate = () => {
        setShowAddModal(false);
        resetForm();
    };

    const openEdit = (chasseur) => {
        setSelectedChasseur(chasseur);

        setForm({
            nom: chasseur?.nom || "",
            description: chasseur?.description || "",
            email: chasseur?.email || "",
            siteweb: chasseur?.siteweb || "",
            domaineIds: chasseur?.domaineIds || [],
            imageFile: null,
            picturePreview: null,
        });

        setShowEditModal(true);
    };

    const closeEdit = () => {
        setShowEditModal(false);
        setSelectedChasseur(null);
        resetForm();
    };

    const openView = (chasseur) => {
        setSelectedChasseur(chasseur);
        setShowViewModal(true);
    };

    const closeView = () => {
        setShowViewModal(false);
        setSelectedChasseur(null);
    };

    const openDelete = (chasseur) => {
        setSelectedChasseur(chasseur);
        setShowDeleteModal(true);
    };

    const closeDelete = () => {
        setShowDeleteModal(false);
        setSelectedChasseur(null);
    };

    const handleFilterChange = (field, value) => {
        setFilters((p) => ({ ...p, [field]: value }));
    };

    const resetFilters = () => setFilters({ nom: "", email: "" });

    const handleDomaineToggle = (id) => {
        setForm((prev) => {
            const exists = prev.domaineIds.includes(id);
            const domaineIds = exists ? prev.domaineIds.filter((x) => x !== id) : [...prev.domaineIds, id];
            return { ...prev, domaineIds };
        });
    };

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onloadend = () => {
            setForm((prev) => ({
                ...prev,
                imageFile: file,
                picturePreview: reader.result,
            }));
        };
        reader.readAsDataURL(file);
    };

    const validateForm = () => {
        if (!form.nom.trim() || !form.email.trim()) {
            alert("Nom et email obligatoires");
            return false;
        }
        if (!form.domaineIds || form.domaineIds.length === 0) {
            alert("Veuillez sélectionner au moins un domaine");
            return false;
        }
        return true;
    };

    const submitCreate = () => {
        if (!validateForm()) return;

        dispatch(
            createChasseurRequest({
                nom: form.nom,
                description: form.description,
                email: form.email,
                siteweb: form.siteweb,
                domaineIds: form.domaineIds,
                imageFile: form.imageFile,
            })
        );

        closeCreate();
    };

    const submitEdit = () => {
        if (!validateForm()) return;

        dispatch(
            updateChasseurRequest(selectedChasseur.id, {
                nom: form.nom,
                description: form.description,
                email: form.email,
                siteweb: form.siteweb,
                domaineIds: form.domaineIds,
                imageFile: form.imageFile,
            })
        );

        closeEdit();
    };

    const confirmDelete = () => {
        dispatch(deleteChasseurRequest(selectedChasseur?.id));
        closeDelete();
    };

    const getImageUrl = (picture) => `${API_BASE_URL}/api/chasseurs/images/${picture}`;

    return (
        <div className="chdPage">
            <div className="chdHeader">
                <div>
                    <h1>Chasseurs de Tête</h1>
                    <p>Gérez vos partenaires de recrutement et cabinets spécialisés</p>
                </div>
            </div>

            {/* Section Filtres */}
            <div className="chdWrap">
                <div className="chdCard">
                    <div className="chdBody">
                        <div className="chdFilterSection">
                            <div className="chdFilterTitle">
                                <Search size={16} />
                                Filtre de recherche
                            </div>

                            <div className="chdFilterGrid">
                                <div className="chdField">
                                    <label>Nom</label>
                                    <input
                                        type="text"
                                        value={filters.nom}
                                        onChange={(e) => handleFilterChange("nom", e.target.value)}
                                        placeholder="Rechercher par nom..."
                                    />
                                </div>

                                <div className="chdField">
                                    <label>Email</label>
                                    <input
                                        type="text"
                                        value={filters.email}
                                        onChange={(e) => handleFilterChange("email", e.target.value)}
                                        placeholder="Rechercher par email..."
                                    />
                                </div>
                            </div>

                            <div className="chdFilterActions">
                                <button className="chdBtn chdBtn--primary" type="button" onClick={openCreate} disabled={loading}>
                                    <Plus size={16} />
                                    Ajouter un chasseur de tête
                                </button>

                                <button
                                    className={`chdBtn ${isAnyFilterFilled ? "chdBtn--warning" : "chdBtn--ghost"}`}
                                    type="button"
                                    onClick={resetFilters}
                                    disabled={!isAnyFilterFilled}
                                >
                                    Réinitialiser les filtres
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Section Liste */}
            <div className="chdWrap">
                <div className="chdCard">
                    <div className="chdBody">
                        <div className="chdTopRow">
                            <h2 className="chdTitle">Liste des chasseurs de tête ({filteredChasseurs.length})</h2>
                        </div>

                        <div className="chdTableWrap">
                            <table className="chdTable">
                                <thead>
                                <tr>
                                    <th>Image</th>
                                    <th>Nom</th>
                                    <th>Description</th>
                                    <th>Email</th>
                                    <th>Site Web</th>
                                    <th>Domaines</th>
                                    <th className="is-right">Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {filteredChasseurs.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} className="chdEmptyCell">
                                            Aucun chasseur de tête trouvé
                                        </td>
                                    </tr>
                                ) : (
                                    filteredChasseurs.map((chasseur) => (
                                        <tr key={chasseur.id}>
                                            <td>
                                                {chasseur.picture ? (
                                                    <div className="chdImagePreview" style={{ width: 44, height: 44 }}>
                                                        <img
                                                            src={getImageUrl(chasseur.picture)}
                                                            alt="chasseur"
                                                            style={{ width: "44px", height: "44px", objectFit: "cover", borderRadius: "10px" }}
                                                        />
                                                    </div>
                                                ) : (
                                                    <div className="chdImagePlaceholder">
                                                        <Users size={24} />
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                          <span className="chdInline">
                            <span className="chdStrong">{chasseur.nom}</span>
                          </span>
                                            </td>
                                            <td className="chdMuted">{chasseur.description || "—"}</td>
                                            <td>
                                                <a href={`mailto:${chasseur.email}`} className="chdLink">
                                                    <Mail size={14} />
                                                    {chasseur.email}
                                                </a>
                                            </td>
                                            <td>
                                                {chasseur.siteweb ? (
                                                    <a
                                                        href={chasseur.siteweb}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="chdLink"
                                                    >
                                                        <LinkIcon size={14} />
                                                        Visiter
                                                    </a>
                                                ) : (
                                                    "—"
                                                )}
                                            </td>
                                            <td>
                                                <div className="chdDomaineTags">
                                                    {(chasseur.domaineIds || []).map((id) => (
                                                        <span key={id} className="chdTag">
                                {domainesById[id]?.nom || domainesById[id]?.reference || `#${id}`}
                              </span>
                                                    ))}
                                                </div>
                                            </td>
                                            <td className="is-right">
                                                <div className="chdRowActions">
                                                    <button className="chdIconBtn" type="button" title="Voir" onClick={() => openView(chasseur)}>
                                                        <Eye size={16} />
                                                    </button>
                                                    <button className="chdIconBtn" type="button" title="Modifier" onClick={() => openEdit(chasseur)}>
                                                        <Edit2 size={16} />
                                                    </button>
                                                    <button
                                                        className="chdIconBtn chdIconBtn--danger"
                                                        type="button"
                                                        title="Supprimer"
                                                        onClick={() => openDelete(chasseur)}
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal Ajout */}
            <Modal
                open={showAddModal}
                title="Nouveau chasseur de tête"
                onClose={closeCreate}
                footer={
                    <>
                        <button className="chdBtn chdBtn--ghost" type="button" onClick={closeCreate}>
                            Annuler
                        </button>
                        <button className="chdBtn chdBtn--primary" type="button" onClick={submitCreate}>
                            Créer
                        </button>
                    </>
                }
            >
                <div className="chdForm">
                    <div className="chdField">
                        <label>Nom *</label>
                        <input
                            type="text"
                            value={form.nom}
                            onChange={(e) => setForm((p) => ({ ...p, nom: e.target.value }))}
                            placeholder="Ex: RecrutPro Consulting"
                        />
                    </div>

                    <div className="chdField">
                        <label>Description</label>
                        <textarea
                            rows={3}
                            value={form.description}
                            onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                            placeholder="Description du cabinet..."
                        />
                    </div>

                    <div className="chdField">
                        <label>Email *</label>
                        <input
                            type="email"
                            value={form.email}
                            onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                            placeholder="contact@exemple.com"
                        />
                    </div>

                    <div className="chdField">
                        <label>Site Web</label>
                        <input
                            type="url"
                            value={form.siteweb}
                            onChange={(e) => setForm((p) => ({ ...p, siteweb: e.target.value }))}
                            placeholder="https://www.exemple.com"
                        />
                    </div>

                    <div className="chdField">
                        <label>Domaines * (sélectionner au moins un)</label>
                        <div className="chdDomaineCheckboxes">
                            {(domaines || []).map((d) => (
                                <label key={d.id} className="chdCheckboxItem">
                                    <input
                                        type="checkbox"
                                        checked={form.domaineIds.includes(d.id)}
                                        onChange={() => handleDomaineToggle(d.id)}
                                    />
                                    <span>{d.nom || d.reference}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    <div className="chdField">
                        <label>Image</label>
                        <div className="chdFileUpload">
                            <input
                                type="file"
                                id="picture-upload"
                                accept="image/*"
                                onChange={handleFileChange}
                                style={{ display: "none" }}
                            />
                            <label htmlFor="picture-upload" className="chdFileUploadBtn">
                                <Upload size={16} />
                                Choisir un fichier
                            </label>

                            {form.picturePreview && (
                                <div className="chdImagePreview">
                                    <img src={form.picturePreview} alt="Aperçu" />
                                    <button
                                        type="button"
                                        className="chdRemoveImage"
                                        onClick={() => setForm((p) => ({ ...p, imageFile: null, picturePreview: null }))}
                                    >
                                        <X size={14} />
                                    </button>
                                </div>
                            )}

                            {form.imageFile && <div className="chdFileName">{form.imageFile.name}</div>}
                        </div>
                    </div>
                </div>
            </Modal>

            {/* Modal Edit */}
            <Modal
                open={showEditModal}
                title={`Modifier — ${selectedChasseur?.nom || ""}`}
                onClose={closeEdit}
                footer={
                    <>
                        <button className="chdBtn chdBtn--ghost" type="button" onClick={closeEdit}>
                            Annuler
                        </button>
                        <button className="chdBtn chdBtn--primary" type="button" onClick={submitEdit}>
                            Enregistrer
                        </button>
                    </>
                }
            >
                <div className="chdForm">
                    <div className="chdField">
                        <label>Nom *</label>
                        <input type="text" value={form.nom} onChange={(e) => setForm((p) => ({ ...p, nom: e.target.value }))} />
                    </div>

                    <div className="chdField">
                        <label>Description</label>
                        <textarea
                            rows={3}
                            value={form.description}
                            onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                        />
                    </div>

                    <div className="chdField">
                        <label>Email *</label>
                        <input type="email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} />
                    </div>

                    <div className="chdField">
                        <label>Site Web</label>
                        <input type="url" value={form.siteweb} onChange={(e) => setForm((p) => ({ ...p, siteweb: e.target.value }))} />
                    </div>

                    <div className="chdField">
                        <label>Domaines * (sélectionner au moins un)</label>
                        <div className="chdDomaineCheckboxes">
                            {(domaines || []).map((d) => (
                                <label key={d.id} className="chdCheckboxItem">
                                    <input
                                        type="checkbox"
                                        checked={form.domaineIds.includes(d.id)}
                                        onChange={() => handleDomaineToggle(d.id)}
                                    />
                                    <span>{d.nom || d.reference}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    <div className="chdField">
                        <label>Image</label>
                        <div className="chdFileUpload">
                            <input
                                type="file"
                                id="picture-upload-edit"
                                accept="image/*"
                                onChange={handleFileChange}
                                style={{ display: "none" }}
                            />
                            <label htmlFor="picture-upload-edit" className="chdFileUploadBtn">
                                <Upload size={16} />
                                Choisir un fichier
                            </label>

                            {form.picturePreview && (
                                <div className="chdImagePreview">
                                    <img src={form.picturePreview} alt="Aperçu" />
                                    <button
                                        type="button"
                                        className="chdRemoveImage"
                                        onClick={() => setForm((p) => ({ ...p, imageFile: null, picturePreview: null }))}
                                    >
                                        <X size={14} />
                                    </button>
                                </div>
                            )}

                            {form.imageFile && <div className="chdFileName">{form.imageFile.name}</div>}
                        </div>
                    </div>
                </div>
            </Modal>

            {/* Modal Aperçu */}
            <Modal
                open={showViewModal}
                title={`Détails — ${selectedChasseur?.nom || ""}`}
                onClose={closeView}
                footer={
                    <button className="chdBtn chdBtn--primary" type="button" onClick={closeView}>
                        Fermer
                    </button>
                }
            >
                {selectedChasseur && (
                    <div className="chdViewContent">
                        <div className="chdViewRow">
                            <div className="chdViewLabel">Nom:</div>
                            <div className="chdViewValue">{selectedChasseur.nom}</div>
                        </div>

                        <div className="chdViewRow">
                            <div className="chdViewLabel">Description:</div>
                            <div className="chdViewValue">{selectedChasseur.description || "—"}</div>
                        </div>

                        <div className="chdViewRow">
                            <div className="chdViewLabel">Email:</div>
                            <div className="chdViewValue">
                                <a href={`mailto:${selectedChasseur.email}`} className="chdLink">
                                    {selectedChasseur.email}
                                </a>
                            </div>
                        </div>

                        <div className="chdViewRow">
                            <div className="chdViewLabel">Site Web:</div>
                            <div className="chdViewValue">
                                {selectedChasseur.siteweb ? (
                                    <a href={selectedChasseur.siteweb} target="_blank" rel="noopener noreferrer" className="chdLink">
                                        {selectedChasseur.siteweb}
                                    </a>
                                ) : (
                                    "—"
                                )}
                            </div>
                        </div>

                        <div className="chdViewRow">
                            <div className="chdViewLabel">Domaines:</div>
                            <div className="chdViewValue">
                                <div className="chdDomaineTags">
                                    {(selectedChasseur.domaineIds || []).map((id) => (
                                        <span key={id} className="chdTag">
                      {domainesById[id]?.nom || domainesById[id]?.reference || `#${id}`}
                    </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </Modal>

            {/* Modal Suppression */}
            <Modal
                open={showDeleteModal}
                title="Confirmation de suppression"
                onClose={closeDelete}
                footer={
                    <>
                        <button className="chdBtn chdBtn--ghost" type="button" onClick={closeDelete}>
                            Annuler
                        </button>
                        <button className="chdBtn chdBtn--danger" type="button" onClick={confirmDelete}>
                            Supprimer
                        </button>
                    </>
                }
            >
                <div className="chdDeleteContent">
                    <p>
                        Voulez-vous vraiment supprimer le chasseur de tête <strong>{selectedChasseur?.nom}</strong> ?
                    </p>
                    <p className="chdWarning">Cette action est irréversible.</p>
                </div>
            </Modal>
        </div>
    );
}
